export function getCaretPositionFromPoint(doc, x, y) {
	if (!doc) {
		return null;
	}
	if (typeof doc.caretPositionFromPoint === 'function') {
		const pos = doc.caretPositionFromPoint(x, y);
		if (pos) {
			return { node: pos.offsetNode, offset: pos.offset };
		}
	} else if (typeof doc.caretRangeFromPoint === 'function') {
		const range = doc.caretRangeFromPoint(x, y);
		if (range) {
			return { node: range.startContainer, offset: range.startOffset };
		}
	}
	return null;
}

export function formatDebugNode(node) {
	if (!node) {
		return '(null)';
	}
	if (node.nodeType === Node.TEXT_NODE) {
		return `#text("${(node.textContent || '').trim().substring(0, 40)}")`;
	}
	const tag = node.tagName || '?';
	const dataType = node.getAttribute ? node.getAttribute('data-type') : '';
	const dataValue = node.getAttribute ? node.getAttribute('data-type-value') : '';
	const id = node.id ? `#${node.id}` : '';
	const cls = node.className ? `.${String(node.className).split(' ')[0]}` : '';
	const attrs = [dataType && `data-type="${dataType}"`, dataValue && `data-type-value="${dataValue}"`].filter(Boolean).join(' ');
	return `<${tag}${id}${cls}${attrs ? ' ' + attrs : ''}>`;
}

export function joinStructuredPath(prefix, path) {
	if (!prefix) {
		return path;
	}
	if (!path) {
		return prefix;
	}
	return `${prefix}.${path}`;
}

export function isStructuredType(type) {
	return type === 'list' || type === 'object';
}

export function replaceRelativePrefix(text, prefix, rootProperties) {
	if (!text || !prefix) {
		return text;
	}

	return text.replace(/\[([^\]:]+)(:[^\]]+)?\]/g, (match, fieldPath, format) => {
		if (!fieldPath || fieldPath.startsWith('$')) {
			return match;
		}

		const firstChunk = fieldPath.split('.')[0];
		if (rootProperties.includes(firstChunk)) {
			return match;
		}

		// Evitar doble-prefijo si el path ya comienza con el prefijo
		if (fieldPath === prefix || fieldPath.startsWith(`${prefix}.`)) {
			return match;
		}

		return `[${joinStructuredPath(prefix, fieldPath)}${format || ''}]`;
	});
}

export function groupOptionsByType(options) {
	const grouped = {
		simple: [],
		object: [],
		list: []
	};

	(options || []).forEach(option => {
		if (option.tipo === 'list') {
			grouped.list.push(option);
		} else if (option.tipo === 'object') {
			grouped.object.push(option);
		} else {
			grouped.simple.push(option);
		}
	});

	return grouped;
}

export function buildScopedOptions(definition) {
	if (!definition || !definition.children || !definition.children.length) {
		return [];
	}

	const prefix = definition.path ? `${definition.path}.` : '';
	const namePrefix = definition.name ? `${definition.name} - ` : '';
	return definition.children.map(child => ({
		content: child.path.replace(prefix, ''),
		name: child.name.replace(namePrefix, ''),
		tipo: child.tipo,
		children: child.children || []
	})).sort((left, right) => left.name.localeCompare(right.name));
}

export function buildStructuredDefaultInnerHTML(option) {
	if (!option) {
		return '[value]';
	}

	if (option.tipo === 'object' && option.children && option.children.length) {
		const hasSimpleChild = option.children.some(child => !isStructuredType(child.tipo));
		if (!hasSimpleChild) {
			return 'Añade los campos que quieras mostrar';
		}
	}

	if (!option.children || !option.children.length) {
		return '[value]';
	}

	for (let index = 0; index < option.children.length; index += 1) {
		const child = option.children[index];
		if (!isStructuredType(child.tipo)) {
			return child.tipo === 'html' ? `[${child.property}:html]` : `[${child.property}]`;
		}
	}

	const firstChild = option.children[0];
	if (firstChild && firstChild.property) {
		return `[${firstChild.property}]`;
	}

	return '[value]';
}

export function buildStructuredBlockHtml(option, isNested = false) {
	const blockClasses = ['valor', 'valor-block'];
	blockClasses.push(option.tipo === 'list' ? 'valor-block-list' : 'valor-block-object');

	// Determinar el data-type-value con el prefijo correcto:
	// - bloques raíz → $root.{path}
	// - bloques anidados (dentro de un iterador u objeto) → $actual.{property}
	let typeValue = option.content;
	if (!typeValue.startsWith('$')) {
		typeValue = isNested ? `$actual.${typeValue}` : `$root.${typeValue}`;
	}

	const attributes = [
		`class="${blockClasses.join(' ')}"`,
		`data-return-type="br"`,
		`data-type="${option.tipo}"`,
		`data-block-type="${option.tipo}"`,
		`data-type-value="${typeValue}"`,
		`data-block-label="${option.name.replace(/"/g, '&quot;')}"`,
	];

	if (option.tipo === 'list') {
		attributes.push('data-recursive-field=""');
		attributes.push('data-not-exist="DELETE"');
		attributes.push('data-condition="true"');
		attributes.push('data-block-separator=", "');
	}

	return `<span ${attributes.join(' ')}>${buildStructuredDefaultInnerHTML(option)}</span>`;
}

export function buildInsertionContent(option, isNested = false) {
	if (!option) {
		return null;
	}
	if (isStructuredType(option.tipo)) {
		const html = buildStructuredBlockHtml(option, isNested);
		return { html, text: html };
	}

	const token = `[${option.content}${option.tipo === 'html' ? ':html' : ''}]`;
	return { html: token, text: token };
}

export function extractPlaceholderAtOffset(text, offset) {
	if (!text || typeof offset !== 'number') {
		return null;
	}
	const regex = /\[([^\]]+)\]/g;
	let match;
	while ((match = regex.exec(text)) !== null) {
		const start = match.index;
		const end = start + match[0].length;
		if (offset >= start && offset <= end) {
			return match[1];
		}
	}
	return null;
}
